"use client";

import { LoaderCircle } from "lucide-react";

import { cn } from "@/lib/utils";

type SaveButtonSpinnerProps = {
  show: boolean;
  size?: "sm" | "default";
  /** Which side gets the animated margin (the side facing its button). */
  margin?: "left" | "right";
  /**
   * For `gap-2` flex parents (e.g. inside a Button): rely on the parent gap
   * for spacing when shown, and pull a -2 margin when hidden so the
   * zero-width spinner doesn't leave a phantom gap slot.
   */
  collapseGap?: boolean;
  className?: string;
  /** Override the icon color (defaults to text-brand-primary). */
  iconClassName?: string;
};

export function SaveButtonSpinner({
  show,
  size = "default",
  margin = "left",
  collapseGap = false,
  className,
  iconClassName,
}: SaveButtonSpinnerProps) {
  const isSmall = size === "sm";
  const shownMargin = collapseGap
    ? margin === "left"
      ? "ml-0"
      : "mr-0"
    : margin === "left"
      ? isSmall
        ? "ml-1.5"
        : "ml-2"
      : isSmall
        ? "mr-1.5"
        : "mr-2";
  const hiddenMargin = collapseGap
    ? margin === "left"
      ? "-ml-2"
      : "-mr-2"
    : margin === "left"
      ? "ml-0"
      : "mr-0";

  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden transition-all duration-300 ease-in-out",
        show
          ? `${shownMargin} ${isSmall ? "w-3.5" : "w-4"} opacity-100`
          : `${hiddenMargin} w-0 opacity-0`,
        className,
      )}
    >
      <LoaderCircle
        className={cn(
          "animate-spin text-brand-primary",
          isSmall ? "size-3.5" : "size-4",
          iconClassName,
        )}
      />
    </span>
  );
}
